"use client";

import { useState, useMemo, useEffect } from "react";
import ProductCard from "@/components/product/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Grid2X2, Grid3X3, SlidersHorizontal } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { cn, formatPrice } from "@/lib/shared/utils";
import { getStock } from "@/lib/commerce/stock";
import { useAnalytics } from "@/lib/analytics/client";
import { AnalyticsEvents, trackClientEvent } from "@/lib/analytics/events";
import type { ProductCardProps } from "@/types/product";
import type { ProductVisibility } from "@/types/drop";

type Product = ProductCardProps & {
  createdAt?: string | Date;
  product_visibility?: ProductVisibility;
  is_featured?: boolean;
};

interface ProductGridProps {
  products: Product[];
  categoryName?: string;
  archiveMode?: boolean;
}

type SortOption =
  "featured" | "price-asc" | "price-desc" | "alpha-asc" | "alpha-desc" | "date-asc" | "date-desc";

type Density = "default" | "compact";

const SORT_LABELS: Record<SortOption, string> = {
  featured: "Featured",
  "price-asc": "Price: Low to High",
  "price-desc": "Price: High to Low",
  "alpha-asc": "Alphabetically: A-Z",
  "alpha-desc": "Alphabetically: Z-A",
  "date-asc": "Oldest First",
  "date-desc": "Newest First",
};

const DENSITY_STORAGE_KEY = "cms:grid-density";

export default function ProductGrid({ products, categoryName, archiveMode }: ProductGridProps) {
  const [sort, setSort] = useState<SortOption>("date-desc");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [density, setDensity] = useState<Density>("default");
  const [availability, setAvailability] = useState<("in" | "out")[]>([]);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [preorderOnly, setPreorderOnly] = useState(false);
  const analytics = useAnalytics();

  useEffect(() => {
    trackClientEvent(analytics, AnalyticsEvents.ProductListViewed, {
      item_list_name: categoryName ?? (archiveMode ? "Archive" : "All Products"),
      item_count: products.length,
      items: products.slice(0, 20).map((p, i) => ({
        item_id: String(p.id),
        item_name: p.name,
        price: p.price,
        index: i,
      })),
    });
  }, [analytics, categoryName, archiveMode, products]);

  useEffect(() => {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored === "default" || stored === "compact") {
      setDensity(stored);
    }
  }, []);

  const handleDensityChange = (next: Density) => {
    setDensity(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      // ignore — private mode / storage disabled
    }
  };

  const { allSizes, minProductPrice, maxProductPrice } = useMemo(() => {
    const prices = products.map((p) => p.price);
    const min = Math.floor(Math.min(...prices, 0));
    const max = Math.ceil(Math.max(...prices, 100));

    const sizes = Array.from(new Set(products.flatMap((p) => p.sizes))).sort();

    return { allSizes: sizes, minProductPrice: min, maxProductPrice: max };
  }, [products]);

  useEffect(() => {
    setPriceRange([minProductPrice, maxProductPrice]);
  }, [minProductPrice, maxProductPrice]);

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    result = result.filter((p) => {
      return p.price >= priceRange[0] && p.price <= priceRange[1];
    });

    if (selectedSizes.length > 0) {
      result = result.filter((p) => p.sizes.some((s) => selectedSizes.includes(s)));
    }

    if (availability.length > 0) {
      result = result.filter((p) => {
        const inStock = getStock(p.stock) > 0;
        if (availability.includes("in") && inStock) return true;
        if (availability.includes("out") && !inStock) return true;
        return false;
      });
    }

    if (lowStockOnly) {
      result = result.filter((p) => {
        const s = getStock(p.stock);
        return s > 0 && s <= (p.low_stock_threshold ?? 3);
      });
    }

    if (featuredOnly || preorderOnly) {
      result = result.filter((p) => {
        if (featuredOnly && p.is_featured === true) return true;
        if (preorderOnly && p.is_preorder === true) return true;
        return false;
      });
    }

    result = result.toSorted((a, b) => {
      switch (sort) {
        case "price-asc":
          return a.price - b.price;
        case "price-desc":
          return b.price - a.price;
        case "alpha-asc":
          return a.name.localeCompare(b.name);
        case "alpha-desc":
          return b.name.localeCompare(a.name);
        case "date-asc": {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        }
        case "date-desc": {
          const descA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const descB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return descB - descA;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [products, sort, priceRange, selectedSizes, availability, lowStockOnly, featuredOnly, preorderOnly]);

  const toggleSize = (size: string) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const toggleAvailability = (value: "in" | "out") => {
    setAvailability((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const filtersContent = (
    <div className="space-y-8">
      {/* Price Filter */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold tracking-wider uppercase">Price</h3>
        <Slider
          defaultValue={[minProductPrice, maxProductPrice]}
          value={priceRange}
          min={minProductPrice}
          max={maxProductPrice}
          step={1}
          onValueChange={(val) => setPriceRange(val as [number, number])}
          className="cursor-pointer py-4"
        />
        <div className="text-muted-foreground flex items-center justify-between text-xs font-medium">
          <span>{formatPrice(priceRange[0])}</span>
          <span>{formatPrice(priceRange[1])}</span>
        </div>
      </div>

      {/* Size Filter */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold tracking-wider uppercase">Size</h3>
        <div className="grid grid-cols-2 gap-2">
          {allSizes.map((size) => (
            <div key={size} className="flex items-center space-x-2">
              <Checkbox
                id={`size-${size}`}
                checked={selectedSizes.includes(size)}
                onCheckedChange={() => toggleSize(size)}
              />
              <Label
                htmlFor={`size-${size}`}
                className="hover:text-muted-foreground cursor-pointer text-sm"
              >
                {size}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Availability Filter */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold tracking-wider uppercase">Availability</h3>
        <div className="grid grid-cols-1 gap-2">
          {(["in", "out"] as const).map((value) => (
            <div key={value} className="flex items-center space-x-2">
              <Checkbox
                id={`availability-${value}`}
                checked={availability.includes(value)}
                onCheckedChange={() => toggleAvailability(value)}
              />
              <Label
                htmlFor={`availability-${value}`}
                className="hover:text-muted-foreground cursor-pointer text-sm"
              >
                {value === "in" ? "In Stock" : "Out of Stock"}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Simple toggle filters */}
      <div className="space-y-2">
        {/* Low Stock Filter */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="low-stock"
            checked={lowStockOnly}
            onCheckedChange={(checked) => setLowStockOnly(checked === true)}
          />
          <Label
            htmlFor="low-stock"
            className="hover:text-muted-foreground cursor-pointer text-sm"
          >
            Low Stock
          </Label>
        </div>

        {/* Featured Filter */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="featured"
            checked={featuredOnly}
            onCheckedChange={(checked) => setFeaturedOnly(checked === true)}
          />
          <Label
            htmlFor="featured"
            className="hover:text-muted-foreground cursor-pointer text-sm"
          >
            Featured
          </Label>
        </div>

        {/* Pre-order Filter */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="preorder"
            checked={preorderOnly}
            onCheckedChange={(checked) => setPreorderOnly(checked === true)}
          />
          <Label
            htmlFor="preorder"
            className="hover:text-muted-foreground cursor-pointer text-sm"
          >
            Pre-order
          </Label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:gap-8 lg:flex-row">
      {/* Mobile Filters Trigger */}
      <div className="mb-4 flex items-center justify-between lg:hidden">
        <Sheet open={isMobileFiltersOpen} onOpenChange={setIsMobileFiltersOpen}>
          <SheetTrigger
            render={
              <button className="hover:text-muted-foreground flex items-center gap-2 text-sm font-bold tracking-wider uppercase">
                <SlidersHorizontal size={16} />
                Filters
              </button>
            }
          />
          <SheetContent side="left" className="w-[300px] sm:w-[400px]">
            <SheetHeader className="mb-8">
              <SheetTitle className="text-left font-black tracking-widest uppercase">
                Filters
              </SheetTitle>
            </SheetHeader>
            {filtersContent}
          </SheetContent>
        </Sheet>

        <span className="text-muted-foreground text-xs font-medium">
          {filteredAndSortedProducts.length} Products
        </span>
      </div>

      {/* Desktop Sidebar */}
      <div className="sticky top-40 hidden w-64 shrink-0 space-y-8 self-start pt-2 lg:block">
        <div className="flex items-center justify-between border-b pb-4">
          <h3 className="text-sm font-black tracking-widest uppercase">Filters</h3>
          {(selectedSizes.length > 0 ||
            priceRange[0] > minProductPrice ||
            priceRange[1] < maxProductPrice ||
            availability.length > 0 ||
            lowStockOnly ||
            featuredOnly ||
            preorderOnly) && (
            <button
              onClick={() => {
                setPriceRange([minProductPrice, maxProductPrice]);
                setSelectedSizes([]);
                setAvailability([]);
                setLowStockOnly(false);
                setFeaturedOnly(false);
                setPreorderOnly(false);
              }}
              className="text-muted-foreground hover:text-foreground text-[10px] tracking-wider uppercase underline"
            >
              Clear All
            </button>
          )}
        </div>
        {filtersContent}
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {/* Toolbar */}
        <div className="flex flex-col gap-4 pb-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-muted-foreground hidden text-xs font-medium tracking-wider uppercase lg:block">
            Showing {filteredAndSortedProducts.length} products
          </div>

          <div className="flex w-full items-center justify-end gap-4 sm:w-auto">
            {/* Density Toggle */}
            <div
              role="group"
              aria-label="Grid density"
              className="border-border hidden items-center border sm:flex"
            >
              <button
                type="button"
                onClick={() => handleDensityChange("default")}
                aria-pressed={density === "default"}
                aria-label="Large grid"
                className={cn(
                  "p-1.5 transition-colors",
                  density === "default"
                    ? "text-background bg-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Grid2X2 size={14} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => handleDensityChange("compact")}
                aria-pressed={density === "compact"}
                aria-label="Compact grid"
                className={cn(
                  "border-border p-1.5 transition-colors",
                  density === "compact"
                    ? "text-background bg-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Grid3X3 size={14} strokeWidth={1.75} />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-muted-foreground text-xs font-bold tracking-wider uppercase">
                Sort By:
              </span>
              <Select
                value={sort}
                onValueChange={(val) => setSort(val as SortOption)}
                itemToStringLabel={(value) => SORT_LABELS[value as SortOption] ?? String(value)}
              >
                <SelectTrigger className="border-border h-8 w-[180px] bg-transparent text-xs font-bold tracking-wider uppercase">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="date-desc">Newest First</SelectItem>
                  <SelectItem value="date-asc">Oldest First</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="alpha-asc">Alphabetically: A-Z</SelectItem>
                  <SelectItem value="alpha-desc">Alphabetically: Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <m.div
          layout
          className={cn(
            "grid gap-x-2",
            density === "compact"
              ? "grid-cols-2 gap-y-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
              : "grid-cols-2 gap-y-10 md:gap-y-12 xl:grid-cols-4"
          )}
        >
          <AnimatePresence mode="popLayout">
            {filteredAndSortedProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                {...product}
                stock={product.stock}
                index={index}
                compact={density === "compact"}
                archiveMode={archiveMode}
                visibility={product.product_visibility}
              />
            ))}
          </AnimatePresence>
        </m.div>

        {filteredAndSortedProducts.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-muted-foreground text-sm tracking-widest uppercase">
              No products found
            </p>
            <button
              onClick={() => {
                setPriceRange([minProductPrice, maxProductPrice]);
                setSelectedSizes([]);
                setAvailability([]);
                setLowStockOnly(false);
                setFeaturedOnly(false);
                setPreorderOnly(false);
              }}
              className="bg-foreground text-background hover:bg-foreground/90 mt-4 px-6 py-3 text-xs font-bold tracking-widest uppercase"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
